import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { ANUploader } from "@answersnow/pkg-theme";

import { space3, space4, space5 } from "../../styled";
import type { AssessmentRecord, PeakFormData } from "../../types";
import { AssessmentDatePicker, FormActionRow, ScoreInputRow } from "./shared";
import { getUser } from "selectors";
import {
  getPeakAgeRange,
  getTypicalAgeScore,
  getFoundationalLearningAgeRange,
  getPerceptualLearningAgeRange,
  getVerbalComprehensionAgeRange,
  getVerbalReasoningMemoryAgeRange,
  getFoundationalLearningBasicSocialAgeRange,
  getAdvancedVerbalComprehensionAdvancedMathAgeRange,
  getVerbalReasoningAdvancedReadingWritingAgeRange,
  getDirectTeachingAgeRange,
  getGeneralizationAgeRange,
  getDifferenceInScore,
  sumScores,
} from "../../util/peakCalculations";

interface PeakDataEntryProps {
  administratorName: string;
  clientAgeInYears: number | null;
  previousAssessment: AssessmentRecord | null;
  onCancel: () => void;
  onSave: (data: PeakFormData) => void;
}

interface PeakModuleUpload {
  moduleKey: "uploadedFilesDt" | "uploadedFilesGen" | "uploadedFilesEq" | "uploadedFilesTr";
  label: string;
}

const PEAK_MODULE_UPLOADS: PeakModuleUpload[] = [
  { moduleKey: "uploadedFilesDt", label: "PEAK-DT Module Triangle" },
  { moduleKey: "uploadedFilesGen", label: "PEAK-G Module Triangle" },
  { moduleKey: "uploadedFilesEq", label: "PEAK-E Module Triangle" },
  { moduleKey: "uploadedFilesTr", label: "PEAK-T Module Triangle" },
];

function readPrev(previous: AssessmentRecord | null, key: string): number | null {
  if (!previous) return null;
  const val = previous.data[key];
  return typeof val === "number" ? val : null;
}

function buildInitialForm(
  previous: AssessmentRecord | null,
  administratorName: string
): PeakFormData {
  return {
    administrationDate: null,
    administratorName,
    foundationalLearning: readPrev(previous, "foundational_learning"),
    perceptualLearning: readPrev(previous, "perceptual_learning"),
    verbalComprehension: readPrev(previous, "verbal_comprehension"),
    verbalReasoningMemory: readPrev(previous, "verbal_reasoning_memory"),
    foundationalLearningBasicSocialSkills: readPrev(
      previous,
      "foundational_learning_basic_social_skills"
    ),
    basicVerbalComprehensionAdvancedSocialSkills: readPrev(
      previous,
      "basic_verbal_comprehension_advanced_social_skills"
    ),
    advancedVerbalComprehensionAdvancedMathSkills: readPrev(
      previous,
      "advanced_verbal_comprehension_advanced_math_skills"
    ),
    verbalReasoningAdvancedReadingWritingSkills: readPrev(
      previous,
      "verbal_reasoning_advanced_reading_writing_skills"
    ),
    reflexivity: readPrev(previous, "reflexivity"),
    symmetry: readPrev(previous, "symmetry"),
    transitivity: readPrev(previous, "transitivity"),
    equivalence: readPrev(previous, "equivalence"),
    nonArbitraryLevel1: readPrev(previous, "non_arbitrary_level_1"),
    culturalLevel2: readPrev(previous, "cultural_level_2"),
    arbitraryLevel3: readPrev(previous, "arbitrary_level_3"),
    complexLevel4: readPrev(previous, "complex_level_4"),
    uploadedFilesDt: [],
    uploadedFilesGen: [],
    uploadedFilesEq: [],
    uploadedFilesTr: [],
  };
}

/**
 * Data entry form for PEAK Comprehensive Assessment.
 * Collects 16 subscale scores across 4 modules (DT, G, E, T) plus optional triangle uploads.
 * Derived fields (Typical Age Score, Age Range Equivalent, Difference in Score, totals)
 * are computed at save-time in DataEntryDialogContent and included in the API payload.
 */
export default function PeakDataEntry({
  administratorName: _administratorName,
  clientAgeInYears,
  previousAssessment,
  onCancel,
  onSave,
}: PeakDataEntryProps) {
  const currentUserName = useSelector(getUser)?.name ?? "";
  const [form, setForm] = useState<PeakFormData>(() =>
    buildInitialForm(previousAssessment, currentUserName)
  );

  // Sync Redux user name to form whenever it changes (for auto-population)
  useEffect(() => {
    setForm((prev) => ({ ...prev, administratorName: currentUserName }));
  }, [currentUserName]);

  function setScore(key: keyof PeakFormData, value: string) {
    const parsed = value === "" ? null : Number(value);
    setForm((prev) => ({ ...prev, [key]: parsed }));
  }

  function handleFileUpload(moduleKey: PeakModuleUpload["moduleKey"]) {
    return ({
      acceptedFiles,
      fileRejections,
    }: {
      acceptedFiles: File[];
      fileRejections: any[];
    }) => {
      const wrappers = [
        ...acceptedFiles.map((file) => ({ file, errors: false })),
        ...fileRejections.map((f) => ({ file: f.file as File, errors: true })),
      ];
      setForm((prev) => ({ ...prev, [moduleKey]: [...prev[moduleKey], ...wrappers] }));
    };
  }

  function handleFileDelete(moduleKey: PeakModuleUpload["moduleKey"]) {
    return (wrapper: { file: File; errors: boolean }) => {
      setForm((prev) => ({
        ...prev,
        [moduleKey]: prev[moduleKey].filter((f) => f !== wrapper),
      }));
    };
  }

  // Derived values — never stored in backend
  const _derived = useMemo(() => {
    const ageRange = getPeakAgeRange(clientAgeInYears);

    // DT subscales
    const flTypical = getTypicalAgeScore("foundationalLearning", ageRange);
    const flAgeRange = getFoundationalLearningAgeRange(form.foundationalLearning);
    const flDiff = getDifferenceInScore(flTypical, form.foundationalLearning);

    const plTypical = getTypicalAgeScore("perceptualLearning", ageRange);
    const plAgeRange = getPerceptualLearningAgeRange(form.perceptualLearning);
    const plDiff = getDifferenceInScore(plTypical, form.perceptualLearning);

    const vcTypical = getTypicalAgeScore("verbalComprehension", ageRange);
    const vcAgeRange = getVerbalComprehensionAgeRange(form.verbalComprehension);
    const vcDiff = getDifferenceInScore(vcTypical, form.verbalComprehension);

    const vrmTypical = getTypicalAgeScore("verbalReasoningMemory", ageRange);
    const vrmAgeRange = getVerbalReasoningMemoryAgeRange(form.verbalReasoningMemory);
    const vrmDiff = getDifferenceInScore(vrmTypical, form.verbalReasoningMemory);

    // DT total = FL score + FL diff + PL score + PL diff (per spec)
    const dtTotal = sumScores([
      form.foundationalLearning,
      form.perceptualLearning,
      form.verbalComprehension,
      form.verbalReasoningMemory,
    ]);
    const dtTypical = getTypicalAgeScore("directTeachingTotal", ageRange);
    const dtAgeRange = getDirectTeachingAgeRange(dtTotal);
    const dtDiff = getDifferenceInScore(dtTypical, dtTotal);

    // Gen subscales
    const flBsTypical = getTypicalAgeScore("foundationalLearningBasicSocialSkills", ageRange);
    const flBsAgeRange = getFoundationalLearningBasicSocialAgeRange(
      form.foundationalLearningBasicSocialSkills
    );
    const flBsDiff = getDifferenceInScore(flBsTypical, form.foundationalLearningBasicSocialSkills);

    const bvTypical = getTypicalAgeScore("basicVerbalComprehensionAdvancedSocialSkills", ageRange);
    // No age range equivalent for this subscale per spec
    const bvDiff = getDifferenceInScore(
      bvTypical,
      form.basicVerbalComprehensionAdvancedSocialSkills
    );

    const avTypical = getTypicalAgeScore("advancedVerbalComprehensionAdvancedMathSkills", ageRange);
    const avAgeRange = getAdvancedVerbalComprehensionAdvancedMathAgeRange(
      form.advancedVerbalComprehensionAdvancedMathSkills
    );
    const avDiff = getDifferenceInScore(
      avTypical,
      form.advancedVerbalComprehensionAdvancedMathSkills
    );

    const vrTypical = getTypicalAgeScore("verbalReasoningAdvancedReadingWritingSkills", ageRange);
    const vrAgeRange = getVerbalReasoningAdvancedReadingWritingAgeRange(
      form.verbalReasoningAdvancedReadingWritingSkills
    );
    const vrDiff = getDifferenceInScore(
      vrTypical,
      form.verbalReasoningAdvancedReadingWritingSkills
    );

    const genTotal = sumScores([
      form.foundationalLearningBasicSocialSkills,
      form.basicVerbalComprehensionAdvancedSocialSkills,
      form.advancedVerbalComprehensionAdvancedMathSkills,
      form.verbalReasoningAdvancedReadingWritingSkills,
    ]);
    const genTypical = getTypicalAgeScore("generalizationTotal", ageRange);
    const genAgeRange = getGeneralizationAgeRange(genTotal);
    const genDiff = getDifferenceInScore(genTypical, genTotal);

    // Equivalence total
    const eqTotal = sumScores([
      form.reflexivity,
      form.symmetry,
      form.transitivity,
      form.equivalence,
    ]);

    // Transformation total
    const tTotal = sumScores([
      form.nonArbitraryLevel1,
      form.culturalLevel2,
      form.arbitraryLevel3,
      form.complexLevel4,
    ]);

    return {
      flTypical,
      flAgeRange,
      flDiff,
      plTypical,
      plAgeRange,
      plDiff,
      vcTypical,
      vcAgeRange,
      vcDiff,
      vrmTypical,
      vrmAgeRange,
      vrmDiff,
      dtTotal,
      dtTypical,
      dtAgeRange,
      dtDiff,
      flBsTypical,
      flBsAgeRange,
      flBsDiff,
      bvTypical,
      bvDiff,
      avTypical,
      avAgeRange,
      avDiff,
      vrTypical,
      vrAgeRange,
      vrDiff,
      genTotal,
      genTypical,
      genAgeRange,
      genDiff,
      eqTotal,
      tTotal,
    };
  }, [form, clientAgeInYears]);

  return (
    <Stack component="form" sx={{ height: "100%" }} noValidate>
      <Stack sx={{ overflowY: "auto", flex: 1, pr: 1 }} rowGap={space5}>
        <TextField
          label="Administrator Name"
          size="small"
          value={form.administratorName}
          onChange={(e) => setForm((prev) => ({ ...prev, administratorName: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />

        <AssessmentDatePicker
          value={form.administrationDate}
          onChange={(date) => setForm((prev) => ({ ...prev, administrationDate: date }))}
        />

        {/* All 16 score inputs — flat, no section groupings per Figma */}
        <Stack rowGap={space4}>
          <ScoreInputRow
            label="Foundational Learning Score"
            value={form.foundationalLearning}
            max={34}
            onChange={(v) => setScore("foundationalLearning", v)}
          />
          <ScoreInputRow
            label="Perceptual Learning Score"
            value={form.perceptualLearning}
            max={22}
            onChange={(v) => setScore("perceptualLearning", v)}
          />
          <ScoreInputRow
            label="Verbal Comprehension Score"
            value={form.verbalComprehension}
            max={100}
            onChange={(v) => setScore("verbalComprehension", v)}
          />
          <ScoreInputRow
            label="Verbal Reasoning & Memory Score"
            value={form.verbalReasoningMemory}
            max={28}
            onChange={(v) => setScore("verbalReasoningMemory", v)}
          />
          <ScoreInputRow
            label="Foundational Learning & Basic Social Skills Score"
            value={form.foundationalLearningBasicSocialSkills}
            max={33}
            onChange={(v) => setScore("foundationalLearningBasicSocialSkills", v)}
          />
          <ScoreInputRow
            label="Basic Verbal Comprehension, Memory, & Advanced Social Skills Score"
            value={form.basicVerbalComprehensionAdvancedSocialSkills}
            max={59}
            onChange={(v) => setScore("basicVerbalComprehensionAdvancedSocialSkills", v)}
          />
          <ScoreInputRow
            label="Advanced Verbal Comprehension, Basic Problem Solving, & Advanced Math Skills Score"
            value={form.advancedVerbalComprehensionAdvancedMathSkills}
            max={63}
            onChange={(v) => setScore("advancedVerbalComprehensionAdvancedMathSkills", v)}
          />
          <ScoreInputRow
            label="Verbal Reasoning, Advanced Problem Solving, & Advanced Reading & Writing Skills Score"
            value={form.verbalReasoningAdvancedReadingWritingSkills}
            max={29}
            onChange={(v) => setScore("verbalReasoningAdvancedReadingWritingSkills", v)}
          />
          <ScoreInputRow
            label="Reflexivity Score"
            value={form.reflexivity}
            max={9}
            onChange={(v) => setScore("reflexivity", v)}
          />
          <ScoreInputRow
            label="Symmetry Score"
            value={form.symmetry}
            max={62}
            onChange={(v) => setScore("symmetry", v)}
          />
          <ScoreInputRow
            label="Transitivity Score"
            value={form.transitivity}
            max={100}
            onChange={(v) => setScore("transitivity", v)}
          />
          <ScoreInputRow
            label="Equivalence Score"
            value={form.equivalence}
            max={28}
            onChange={(v) => setScore("equivalence", v)}
          />
          <ScoreInputRow
            label="Non-Arbitrary (Level 1) Score"
            value={form.nonArbitraryLevel1}
            max={34}
            onChange={(v) => setScore("nonArbitraryLevel1", v)}
          />
          <ScoreInputRow
            label="Cultural (Level 2) Score"
            value={form.culturalLevel2}
            max={22}
            onChange={(v) => setScore("culturalLevel2", v)}
          />
          <ScoreInputRow
            label="Arbitrary (Level 3) Score"
            value={form.arbitraryLevel3}
            max={100}
            onChange={(v) => setScore("arbitraryLevel3", v)}
          />
          <ScoreInputRow
            label="Complex (Level 4) Score"
            value={form.complexLevel4}
            max={28}
            onChange={(v) => setScore("complexLevel4", v)}
          />
        </Stack>

        {/* Per-module document uploads */}
        <Stack rowGap={space5}>
          {PEAK_MODULE_UPLOADS.map(({ moduleKey, label }) => (
            <Stack key={moduleKey} rowGap={space3}>
              <Typography variant="body2" fontWeight={500}>
                {label}
              </Typography>
              <Box>
                <ANUploader
                  dropzoneFiles={form[moduleKey]}
                  alreadyUploadedFiles={[]}
                  handleFileUpload={handleFileUpload(moduleKey)}
                  handleFileDelete={handleFileDelete(moduleKey)}
                  handleLoadedFileDelete={() => {}}
                  maxFileSize={3 * 1024 * 1024}
                  useDropzoneOptions={{
                    accept: {
                      "image/png": [],
                      "image/jpeg": [],
                      "application/pdf": [],
                    },
                    multiple: false,
                  }}
                />
              </Box>
            </Stack>
          ))}
        </Stack>
      </Stack>
      <Box sx={{ pt: space3, flexShrink: 0 }}>
        <FormActionRow
          onCancel={onCancel}
          onSubmit={() => onSave(form)}
          submitLabel="Add Assessment"
        />
      </Box>
    </Stack>
  );
}
